import { ActionIcon, Badge, Group, Table, Text, Tooltip } from "@mantine/core";
import { IconEdit, IconGripVertical, IconTrash } from "@tabler/icons-react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";

export const typeBadgeColor: Record<string, string> = {
  STRING: "blue",
  NUMBER: "cyan",
  BOOLEAN: "green",
  DATE: "grape",
  EMAIL: "violet",
  PHONE: "indigo",
  URL: "teal",
  COUNTRY: "orange",
  SELECT: "pink",
  MULTI_SELECT: "red",
};

export interface CustomFieldRow {
  id: string;
  name: string;
  description?: string;
  propertyKey: string;
  type: string;
  required: boolean;
  unique: boolean;
  visibility: string;
  editability: string;
}

interface CustomFieldsTableProps {
  fields: CustomFieldRow[];
  onDragEnd: (result: DropResult) => void;
  onEdit: (index: number) => void;
  onDelete: (index: number, name: string) => void;
  droppableId: string;
}

export const CustomFieldsTable = ({
  fields,
  onDragEnd,
  onEdit,
  onDelete,
  droppableId,
}: CustomFieldsTableProps) => (
  <DragDropContext onDragEnd={onDragEnd}>
    <Table striped highlightOnHover>
      <thead>
        <tr>
          <th style={{ width: 40 }} />
          <th>Name</th>
          <th>Key</th>
          <th>Type</th>
          <th>Required</th>
          <th>Unique</th>
          <th>Visibility</th>
          <th>Editability</th>
          <th style={{ width: 100 }}>Actions</th>
        </tr>
      </thead>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <tbody ref={provided.innerRef} {...provided.droppableProps}>
            {fields.map((field, index) => (
              <Draggable key={field.id} draggableId={field.id} index={index}>
                {(provided) => (
                  <tr ref={provided.innerRef} {...provided.draggableProps}>
                    <td>
                      <div {...provided.dragHandleProps}>
                        <IconGripVertical
                          size={16}
                          style={{ cursor: "grab" }}
                        />
                      </div>
                    </td>
                    <td>
                      <Text fw={500}>{field.name}</Text>
                      {field.description && (
                        <Text size="xs" c="dimmed">
                          {field.description}
                        </Text>
                      )}
                    </td>
                    <td>
                      <Text size="sm" ff="monospace">
                        {field.propertyKey}
                      </Text>
                    </td>
                    <td>
                      <Badge
                        color={typeBadgeColor[field.type] ?? "gray"}
                        variant="light"
                        size="sm"
                      >
                        {field.type}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        color={field.required ? "red" : "gray"}
                        variant="light"
                        size="sm"
                      >
                        {field.required ? "Yes" : "No"}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        color={field.unique ? "orange" : "gray"}
                        variant="light"
                        size="sm"
                      >
                        {field.unique ? "Yes" : "No"}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant="outline" size="sm">
                        {field.visibility}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant="outline" size="sm">
                        {field.editability}
                      </Badge>
                    </td>
                    <td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="Edit">
                          <ActionIcon
                            color="blue"
                            variant="subtle"
                            onClick={() => onEdit(index)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => onDelete(index, field.name)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </td>
                  </tr>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </tbody>
        )}
      </Droppable>
    </Table>
  </DragDropContext>
);
